// Unicode text styling engine
const cp = (n) => String.fromCodePoint(n);

const mathStyle = (upper, lower, digits, exceptions = {}) => (ch) => {
  if (exceptions[ch]) return exceptions[ch];
  const c = ch.charCodeAt(0);
  if (upper != null && c >= 65 && c <= 90) return cp(upper + c - 65);
  if (lower != null && c >= 97 && c <= 122) return cp(lower + c - 97);
  if (digits != null && c >= 48 && c <= 57) return cp(digits + c - 48);
  return ch;
};

const mapFromStrings = (from, to) => {
  const f = Array.from(from);
  const t = Array.from(to);
  const m = {};
  f.forEach((ch, i) => (m[ch] = t[i] ?? ch));
  return (ch) => m[ch] ?? ch;
};

const ALPHA_L = 'abcdefghijklmnopqrstuvwxyz';
const ALPHA_U = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export const charMaps = {
  serifBold: mathStyle(0x1d400, 0x1d41a, 0x1d7ce),
  italic: mathStyle(0x1d434, 0x1d44e, null, { h: '\u210e' }),
  boldItalic: mathStyle(0x1d468, 0x1d482),
  script: mathStyle(0x1d49c, 0x1d4b6, null, {
    B: '\u212c', E: '\u2130', F: '\u2131', H: '\u210b', I: '\u2110', L: '\u2112',
    M: '\u2133', R: '\u211b', e: '\u212f', g: '\u210a', o: '\u2134',
  }),
  boldScript: mathStyle(0x1d4d0, 0x1d4ea),
  fraktur: mathStyle(0x1d504, 0x1d51e, null, {
    C: '\u212d', H: '\u210c', I: '\u2111', R: '\u211c', Z: '\u2128',
  }),
  doubleStruck: mathStyle(0x1d538, 0x1d552, 0x1d7d8, {
    C: '\u2102', H: '\u210d', N: '\u2115', P: '\u2119', Q: '\u211a', R: '\u211d', Z: '\u2124',
  }),
  boldFraktur: mathStyle(0x1d56c, 0x1d586),
  sans: mathStyle(0x1d5a0, 0x1d5ba, 0x1d7e2),
  sansBold: mathStyle(0x1d5d4, 0x1d5ee, 0x1d7ec),
  sansItalic: mathStyle(0x1d608, 0x1d622),
  sansBoldItalic: mathStyle(0x1d63c, 0x1d656),
  monospace: mathStyle(0x1d670, 0x1d68a, 0x1d7f6),
  fullwidth: (ch) => {
    if (ch === ' ') return '\u3000';
    const c = ch.charCodeAt(0);
    if (c >= 33 && c <= 126) return cp(0xff01 + c - 33);
    return ch;
  },
  circled: (ch) => {
    const c = ch.charCodeAt(0);
    if (c >= 65 && c <= 90) return cp(0x24b6 + c - 65);
    if (c >= 97 && c <= 122) return cp(0x24d0 + c - 97);
    if (ch === '0') return '\u24ea';
    if (c >= 49 && c <= 57) return cp(0x2460 + c - 49);
    return ch;
  },
  circledFilled: (ch) => {
    const u = ch.toUpperCase().charCodeAt(0);
    if (u >= 65 && u <= 90) return cp(0x1f150 + u - 65);
    return ch;
  },
  squared: (ch) => {
    const u = ch.toUpperCase().charCodeAt(0);
    if (u >= 65 && u <= 90) return cp(0x1f130 + u - 65);
    return ch;
  },
  squaredFilled: (ch) => {
    const u = ch.toUpperCase().charCodeAt(0);
    if (u >= 65 && u <= 90) return cp(0x1f170 + u - 65);
    return ch;
  },
  regional: (ch) => {
    const u = ch.toUpperCase().charCodeAt(0);
    if (u >= 65 && u <= 90) return cp(0x1f1e6 + u - 65) + '\u200b';
    return ch;
  },
  smallCaps: mapFromStrings(ALPHA_L + ALPHA_U, '\u1d00\u0299\u1d04\u1d05\u1d07\ua730\u0262\u029c\u026a\u1d0a\u1d0b\u029f\u1d0d\u0274\u1d0f\u1d18\u01eb\u0280\ua731\u1d1b\u1d1c\u1d20\u1d21x\u028f\u1d22'.repeat(2)),
  superscript: mapFromStrings(ALPHA_L + ALPHA_U + '0123456789',
    '\u1d43\u1d47\u1d9c\u1d48\u1d49\u1da0\u1d4d\u02b0\u2071\u02b2\u1d4f\u02e1\u1d50\u207f\u1d52\u1d56q\u02b3\u02e2\u1d57\u1d58\u1d5b\u02b7\u02e3\u02b8\u1dbb' +
    '\u1d2c\u1d2e\u1d9c\u1d30\u1d31\u1da0\u1d33\u1d34\u1d35\u1d36\u1d37\u1d38\u1d39\u1d3a\u1d3c\u1d3eQ\u1d3f\u02e2\u1d40\u1d41\u2c7d\u1d42\u02e3\u02b8\u1dbb' +
    '\u2070\u00b9\u00b2\u00b3\u2074\u2075\u2076\u2077\u2078\u2079'),
  subscript: mapFromStrings(ALPHA_L + '0123456789',
    '\u2090bcd\u2091fg\u2095\u1d62\u2c7c\u2096\u2097\u2098\u2099\u2092\u209aq\u1d63\u209b\u209c\u1d64\u1d65w\u2093yz' +
    '\u2080\u2081\u2082\u2083\u2084\u2085\u2086\u2087\u2088\u2089'),
  currency: mapFromStrings(ALPHA_U + ALPHA_L,
    '\u20b3\u0e3f\u20b5\u0110\u0246\u20a3\u20b2\u2c67\u0142J\u20ad\u2c60\u20a5\u20a6\u00d8\u20b1Q\u2c64\u20b4\u20ae\u0244V\u20a9\u04fe\u024e\u2c6b'.repeat(2)),
  greek: mapFromStrings(ALPHA_U + ALPHA_L,
    '\u039b\u0392\u1103\u0110\u03a3F\u0393\u0389I\u0408\u049c\u1102M\u041f\u04e8\u0420\u01aa\u042f\u01a7\u01ac\u0426V\u0429X\u03a5Z'.repeat(2)),
  slavic: mapFromStrings(ALPHA_U + ALPHA_L,
    '\u0414\u0411\u0480D\u0404F\u0411\u041d\u0407\u0408\u040cL\u041c\u0418\u0424P\u01eaR\u0405\u0413\u0426V\u0429\u0416\u0427Z'.repeat(2)),
  runic: mapFromStrings(ALPHA_U + ALPHA_L,
    '\u16a8\u16d2\u16b2\u16de\u16d6\u16a0\u16b7\u16bb\u16c1\u16c3\u16b2\u16da\u16d7\u16be\u16df\u16c8\u16e9\u16b1\u16cb\u16cf\u16a2\u16a1\u16b9\u16ea\u16c3\u16c9'.repeat(2)),
  oldItalic: mapFromStrings(ALPHA_U + ALPHA_L,
    '\ud800\udf00\ud800\udf01\ud800\udf02\ud800\udf03\ud800\udf04\ud800\udf05\ud800\udf06\ud800\udf07\ud800\udf09\ud800\udf09\ud800\udf0a\ud800\udf0b\ud800\udf0c\ud800\udf0d\ud800\udf0f\ud800\udf10\ud800\udf12\ud800\udf13\ud800\udf14\ud800\udf15\ud800\udf16\ud800\udf1a\ud800\udf1a\ud800\udf17\ud800\udf16\ud800\udf06'.repeat(2)),
  exotic: mapFromStrings(ALPHA_U + ALPHA_L,
    '\u0104\u0181\u010c\u010e\u0118\u0191\u0122\u0124\u012e\u0134\u0136\u0141\u039c\u0147\u00d8\u01a4\u01ea\u0158\u015e\u0162\u01b2V\u0174\u0416\u00a5\u017d' +
    '\u0105\u0180\u010d\u010f\u0119\u0192\u0123\u0125\u012f\u0135\u0137\u0142m\u0148\u00f8\u01a5\u01eb\u0159\u015f\u0163\u0173v\u0175x\u00fd\u017e'),
  accented: mapFromStrings(ALPHA_U + ALPHA_L,
    '\u00c1\u1e02\u0106\u010e\u00c9\u1e1e\u01f4\u1e26\u00cd\u0134\u1e30\u0139\u1e3e\u0143\u00d3\u1e54Q\u0154\u015a\u0164\u00da\u1e7c\u1e82X\u00dd\u0179' +
    '\u00e1\u1e03\u0107\u010f\u00e9\u1e1f\u01f5\u1e27\u00ed\u0135\u1e31\u013a\u1e3f\u0144\u00f3\u1e55q\u0155\u015b\u0165\u00fa\u1e7d\u1e83x\u00fd\u017a'),
  cherokee: mapFromStrings(ALPHA_U + ALPHA_L,
    '\u13aa\u13f4\u13df\u13a0\u13ac\u13d3\u13f6\u13bb\u13a5\u13ab\u13e6\u13de\u13b7\u13c1\u13be\u13e2\u13a4\u13a1\u13da\u13a2\u13ec\u13d9\u13b3\u13c9\u13a9\u13c3'.repeat(2)),
};

const FLIP = {
  a: '\u0250', b: 'q', c: '\u0254', d: 'p', e: '\u01dd', f: '\u025f', g: '\u0183', h: '\u0265', i: '\u0131', j: '\u027e',
  k: '\u029e', l: 'l', m: '\u026f', n: 'u', o: 'o', p: 'd', q: 'b', r: '\u0279', s: 's', t: '\u0287', u: 'n', v: '\u028c',
  w: '\u028d', x: 'x', y: '\u028e', z: 'z', A: '\u2200', B: '\ua4ed', C: '\u0186', D: '\u15e1', E: '\u018e', F: '\u2132',
  G: '\u2141', H: 'H', I: 'I', J: '\u017f', K: '\ua4d8', L: '\u02e5', M: 'W', N: 'N', O: 'O', P: '\u0500', Q: '\u038c',
  R: '\ua4e4', S: 'S', T: '\u22a5', U: '\u2229', V: '\u039b', W: 'M', X: 'X', Y: '\u2144', Z: 'Z', '1': '\u21c2',
  '2': '\u1105', '3': '\u0190', '4': '\u3123', '5': '\u078e', '6': '9', '7': '\u3125', '8': '8', '9': '6', '0': '0',
  '.': '\u02d9', ',': "'", "'": ',', '?': '\u00bf', '!': '\u00a1', '(': ')', ')': '(', '[': ']', ']': '[', '<': '>', '>': '<', _: '\u203e',
};

const MIRROR = { b: 'd', d: 'b', p: 'q', q: 'p', '(': ')', ')': '(', '[': ']', ']': '[', '{': '}', '}': '{', '<': '>', '>': '<', '/': '\\', '\\': '/', s: '\ua645', S: '\ua644', z: '\u01b9', Z: '\u01b8', E: '\u018e', J: '\u10b1', R: '\u042f', N: '\u0418', '?': '\u2e2e' };

export const chars = (s) => Array.from(s);

// Letters that don't decompose under NFD but have a plain ASCII base
const LIGATURES = {
  ß: 'ss', ẞ: 'SS', æ: 'ae', Æ: 'AE', œ: 'oe', Œ: 'OE', ø: 'o', Ø: 'O',
  đ: 'd', Đ: 'D', ð: 'd', Ð: 'D', þ: 'th', Þ: 'TH', ł: 'l', Ł: 'L',
  ħ: 'h', Ħ: 'H', ı: 'i', ŋ: 'n', Ŋ: 'N',
};
// Decompose accented letters, map their base and retain combining marks.
// This preserves spelling; the receiving font still controls mark placement.
export const applyMap = (text, fn) => chars(text.normalize('NFD')).map((c) => {
  if (LIGATURES[c]) return chars(LIGATURES[c]).map(fn).join('');
  return fn(c);
}).join('');
export const combine = (text, mark) => chars(text).map((c) => (c === ' ' || c === '\n' ? c : c + mark)).join('');
export const flipText = (text) => chars(text).map((c) => FLIP[c] ?? c).reverse().join('');
export const reverseText = (text) => chars(text).reverse().join('');
export const mirrorText = (text) => chars(text).map((c) => MIRROR[c] ?? c).reverse().join('');
export const spaceText = (text) => chars(text).join(' ');
export const alternate = (text) => {
  let i = 0;
  return chars(text).map((c) => (/[a-z]/i.test(c) ? (i++ % 2 === 0 ? c.toLowerCase() : c.toUpperCase()) : c)).join('');
};
export const titleCase = (t) => t.toLowerCase().replace(/(^|\s)(\S)/g, (m, a, b) => a + b.toUpperCase());
export const applyCase = (t, mode) => {
  if (mode === 'upper') return t.toUpperCase();
  if (mode === 'lower') return t.toLowerCase();
  if (mode === 'title') return titleCase(t);
  if (mode === 'alt') return alternate(t);
  return t;
};

// Deterministic zalgo
const UP = [0x030d, 0x030e, 0x0304, 0x0305, 0x033f, 0x0311, 0x0306, 0x0310, 0x0352, 0x0357, 0x0351, 0x0307, 0x0308, 0x030a, 0x0342, 0x0343, 0x0344, 0x034a, 0x034b, 0x034c, 0x0303, 0x0302, 0x030c, 0x0350, 0x0300, 0x0301, 0x030b, 0x030f, 0x0312, 0x0313, 0x0314, 0x033d, 0x0309, 0x0363, 0x0364, 0x0365, 0x0366, 0x0367, 0x0368, 0x0369, 0x036a, 0x036b, 0x036c, 0x036d, 0x036e, 0x036f, 0x033e, 0x035b];
const DOWN = [0x0316, 0x0317, 0x0318, 0x0319, 0x031c, 0x031d, 0x031e, 0x031f, 0x0320, 0x0324, 0x0325, 0x0326, 0x0329, 0x032a, 0x032b, 0x032c, 0x032d, 0x032e, 0x032f, 0x0330, 0x0331, 0x0332, 0x0333, 0x0339, 0x033a, 0x033b, 0x033c, 0x0345, 0x0347, 0x0348, 0x0349, 0x034d, 0x034e, 0x0353, 0x0354, 0x0355, 0x0356, 0x0359, 0x035a, 0x0323];
const MID = [0x0315, 0x031b, 0x0340, 0x0341, 0x0358, 0x0321, 0x0322, 0x0327, 0x0328, 0x0334, 0x0335, 0x0336, 0x034f, 0x035c, 0x035d, 0x035e, 0x035f, 0x0360, 0x0362, 0x0338, 0x0337, 0x0361, 0x0489];
export const zalgo = (text, level = 3) => {
  let seed = 7;
  const rnd = (n) => { seed = (seed * 9301 + 49297) % 233280; return Math.floor((seed / 233280) * n); };
  return chars(text).map((c) => {
    if (c === ' ' || c === '\n') return c;
    let out = c;
    for (let i = 0; i < level; i++) out += cp(UP[rnd(UP.length)]);
    for (let i = 0; i < Math.ceil(level / 2); i++) out += cp(MID[rnd(MID.length)]);
    for (let i = 0; i < level; i++) out += cp(DOWN[rnd(DOWN.length)]);
    return out;
  }).join('');
};

export const between = (text, sep, keepSpace = true) =>
  chars(text).map((c) => (c === ' ' && keepSpace ? ' ' : c)).join(sep);
export const wrapEach = (text, l, r) => chars(text).map((c) => (c === ' ' ? ' ' : l + c + r)).join('');

export const M = {
  strike: '\u0336', underline: '\u0332', wavy: '\u0330', slash: '\u0338', stacked: '\u0347',
  halo: '\u0325', crossed: '\u20e0', arrow: '\u20d1', tilde: '\u0334', sparkle: '\u0489', short: '\u0335',
};
